// The default name a search is recorded under, rebuilt as the operator changes
// the site, radius or area until they type over it.
//
// Format: "Near [Site] ([distance], [size]+)". A viewport ("Search this area")
// search has no radius, so it omits the distance rather than inventing one, and
// reads "Mapped area" rather than "Near" anything.
//
// Returns "" until there is somewhere to search from. A name built out of the
// radius and area alone would describe every search the operator ever ran from
// this form, and the name is the search's identity - so the next run would
// silently replace the last one's stored rows.
//
// Pure so both the field and its test can use it, and so the BE never has to
// guess a name - the frontend always sends one.

export interface SearchNameInputs {
  // The anchor site's name, with the operational-status suffix already stripped.
  siteName: string | null;
  // Falls back to the typed outcode when no site is picked.
  postcodeOutward: string;
  radiusKm: string;
  minFloorAreaSqm: string;
  // True for a map-viewport search, which has an area but no radius.
  isViewport: boolean;
}

const formatArea = (raw: string): string | null => {
  const n = Number(raw);
  if (!raw.trim() || !Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString("en-GB")}m²+`;
};

const formatRadius = (raw: string): string | null => {
  const n = Number(raw);
  if (!raw.trim() || !Number.isFinite(n) || n <= 0) return null;
  return `${n}km`;
};

export const deriveSearchName = ({
  siteName,
  postcodeOutward,
  radiusKm,
  minFloorAreaSqm,
  isViewport,
}: SearchNameInputs): string => {
  const anchor = siteName?.trim() || postcodeOutward.trim().toUpperCase();
  if (!isViewport && !anchor) return "";

  const where = isViewport ? "Mapped area" : `Near ${anchor}`;

  // A viewport search covers whatever the operator panned to, so a radius would
  // describe something that was never applied.
  const parts = [isViewport ? null : formatRadius(radiusKm), formatArea(minFloorAreaSqm)].filter(
    (p): p is string => p !== null
  );

  return parts.length > 0 ? `${where} (${parts.join(", ")})` : where;
};
